import { Form, type FormInstance } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomInput, CustomSelect, CustomTypography } from "sipli-fleet-core/components";
import {
  useQueryGetUnitBrandLookupByType,
  useQueryGetUnitModelLookupByMake,
} from "src/queries/administrationSetup.queries";
import {
  useQueryGetWarehouseUnitHwStatusLookup,
  useQueryGetWarehouseUnitOperationTypeLookup,
} from "src/queries/warehouseUnits.queries";
import {
  findEntryById,
  isFuelSensorType,
  toAdminOptions,
  toHwStatusOptions,
  toOperationTypeOptions,
  useUnitTypeLookup,
  type UnitFormValues,
} from "./unitFormShared";

export interface UnitInfoFieldsProps {
  form: FormInstance<UnitFormValues>;
  open: boolean;
}

const UnitInfoFields = ({ form, open }: UnitInfoFieldsProps) => {
  const { t } = useTranslation();
  const selectedUnitTypeId = Form.useWatch("unitTypeId", form);
  const selectedMakeId = Form.useWatch("makeId", form);

  const { items: unitTypeItems, options: unitTypeOptions, isLoading: isUnitTypeLoading } =
    useUnitTypeLookup(open);
  const selectedUnitTypeEntry = findEntryById(unitTypeItems, selectedUnitTypeId);
  const isFuelSensor = isFuelSensorType(selectedUnitTypeEntry);

  const { data: makeData, isLoading: isMakeLoading } = useQueryGetUnitBrandLookupByType(
    selectedUnitTypeId,
    open
  );
  const { data: modelData, isLoading: isModelLoading } = useQueryGetUnitModelLookupByMake(
    selectedMakeId,
    selectedUnitTypeId,
    open
  );
  const { data: hwStatusData, isLoading: isHwStatusLoading } =
    useQueryGetWarehouseUnitHwStatusLookup({ enabled: open });
  const { data: operationTypeData, isLoading: isOperationTypeLoading } =
    useQueryGetWarehouseUnitOperationTypeLookup({ enabled: open });

  const makeOptions = useMemo(() => toAdminOptions(makeData?.data?.response ?? []), [makeData]);
  const modelOptions = useMemo(() => toAdminOptions(modelData?.data?.response ?? []), [modelData]);
  const hwStatusOptions = useMemo(
    () => toHwStatusOptions(hwStatusData?.data?.response ?? [], t),
    [hwStatusData, t]
  );
  const operationTypeOptions = useMemo(
    () => toOperationTypeOptions(operationTypeData?.data?.response ?? [], t),
    [operationTypeData, t]
  );

  const handleUnitTypeChange = () => {
    form.setFieldsValue({ makeId: undefined, modelId: undefined });
  };

  const handleMakeChange = () => {
    form.setFieldValue("modelId", undefined);
  };

  return (
    <div className="flex flex-col gap-x-3">
      <Form.Item name="unitTypeId" label={t("unit_type")}>
        <CustomSelect
          placeholder={t("select")}
          options={unitTypeOptions}
          loading={isUnitTypeLoading}
          onChange={handleUnitTypeChange}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>

      <div className="grid gap-x-3 md:grid-cols-2">
        <Form.Item name="makeId" label={t("brand")}>
          <CustomSelect
            placeholder={t("select")}
            options={makeOptions}
            loading={isMakeLoading}
            disabled={!selectedUnitTypeId}
            onChange={handleMakeChange}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="modelId"
          label={`${t("model_name")}*`}
          rules={[{ required: true, message: t("field_required") }]}
        >
          <CustomSelect
            placeholder={t("select")}
            options={modelOptions}
            loading={isModelLoading}
            disabled={!selectedMakeId}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      </div>

      {isFuelSensor ? (
        <div className="grid gap-x-3 md:grid-cols-2">
          <Form.Item name="length" label={t("length")}>
            <CustomInput placeholder={t("type")} />
          </Form.Item>
          <Form.Item
            name="serial"
            label={`${t("serial_number")}*`}
            rules={[
              { required: true, message: t("field_required") },
              { pattern: /^[a-zA-Z0-9_.-]{8,18}$/, message: t("invalid_serial_number") },
            ]}
          >
            <CustomInput placeholder={t("type")} />
          </Form.Item>
        </div>
      ) : (
        <>
          <div className="grid gap-x-3 md:grid-cols-2">
            <Form.Item
              name="imei"
              label={`${t("imei")}*`}
              rules={[
                { required: true, message: t("field_required") },
                { pattern: /^[0-9.-]{12,18}$/, message: t("invalid_imei") },
              ]}
            >
              <CustomInput placeholder={t("type")} />
            </Form.Item>
            <Form.Item
              name="serial"
              label={`${t("serial_number")}*`}
              rules={[
                { required: true, message: t("field_required") },
                { pattern: /^[a-zA-Z0-9_.-]{8,18}$/, message: t("invalid_serial_number") },
              ]}
            >
              <CustomInput placeholder={t("type")} />
            </Form.Item>
          </div>
          <Form.Item name="phoneNumber" label={t("sim_number")} className="md:w-1/2">
            <CustomInput placeholder={t("type")} />
          </Form.Item>
        </>
      )}

      <div className="mt-6 flex flex-col gap-3 bg-gray1 p-4">
        <CustomTypography.CustomText className="text-lg! font-bold! text-gray8!">
          {t("status")}
        </CustomTypography.CustomText>

        <div className={`grid gap-x-3 ${isFuelSensor ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          <Form.Item name="operationTypeId" label={t("operation_type")} className="mb-0!">
            <CustomSelect
              placeholder={t("select")}
              options={operationTypeOptions}
              loading={isOperationTypeLoading}
            />
          </Form.Item>
          {isFuelSensor ? null : (
            <Form.Item name="active" label={t("configuration_status")} className="mb-0!">
              <CustomSelect
                placeholder={t("select")}
                options={[
                  { value: "true", label: t("active") },
                  { value: "false", label: t("inactive") },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item name="unitStatusId" label={t("hw_status")} className="mb-0!">
            <CustomSelect
              placeholder={t("select")}
              options={hwStatusOptions}
              loading={isHwStatusLoading}
            />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export default UnitInfoFields;
